import { useState, useEffect, useRef } from 'react';

export interface UseLoadScriptOptions {
  /**
   * Nonce applied to GSI script tag. Propagates to inline style tag
   */
  src: string;
  /**
   * Nonce applied to GSI script tag. Propagates to inline style tag
   */
  nonce?: string;
  /**
   * Callback fires on load [gsi](https://accounts.google.com/gsi/client) script success
   */
  onScriptLoadSuccess?: () => void;
  /**
   * Callback fires on load [gsi](https://accounts.google.com/gsi/client) script failure
   */
  onScriptLoadError?: () => void;
}

export default function useLoadScript({
  src,
  nonce,
  onScriptLoadSuccess,
  onScriptLoadError,
}: UseLoadScriptOptions): boolean {
  const [scriptLoadedSuccessfully, setScriptLoadedSuccessfully] =
    useState<boolean>(false);

  const onScriptLoadSuccessRef = useRef(onScriptLoadSuccess);
  onScriptLoadSuccessRef.current = onScriptLoadSuccess;

  const onScriptLoadErrorRef = useRef(onScriptLoadError);
  onScriptLoadErrorRef.current = onScriptLoadError;

  useEffect(() => {
    const scriptTag = document.createElement('script');
    scriptTag.src = src;
    scriptTag.async = true;
    scriptTag.defer = true;
    scriptTag.nonce = nonce;
    scriptTag.onload = () => {
      setScriptLoadedSuccessfully(true);
      onScriptLoadSuccessRef.current?.();
    };
    scriptTag.onerror = () => {
      setScriptLoadedSuccessfully(false);
      onScriptLoadErrorRef.current?.();
    };

    document.body.appendChild(scriptTag);

    return () => {
      document.body.removeChild(scriptTag);
    };
  }, [src, nonce]);

  return scriptLoadedSuccessfully;
}
